import Agilite from 'agilite'
import Axios from 'axios'
import { createLookup, extractAddressDetails, generateTitle } from '../../lib/utils'
import store from '../../../store'
import { geocodeByAddress } from 'react-google-places-autocomplete'
import dayjs from 'dayjs'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

export const getTests = async (filter = {}, page, pageLimit, projection = '') => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await agilite.Connectors.execute('ampath_tests', 'read', {
          filter: JSON.stringify(filter),
          page: page,
          pageLimit: pageLimit,
          projection: projection
        })

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const readLabOrders = async (filter = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      const { searchText, status, dateRange, priority, ...otherFilters } = filter

      // Build the initial match stage
      let matchStage = { ...otherFilters }

      // Add date range filtering if provided
      if (dateRange && dateRange.length === 2) {
        matchStage.createdAt = {
          $gte: new Date(dateRange[0]).toISOString(),
          $lte: new Date(dateRange[1]).toISOString()
        }
      }

      // Add priority filtering if provided
      if (priority) {
        matchStage['orderData.priority'] = priority
      }

      const pipeline = [{ $match: matchStage }, { $sort: { createdAt: -1 } }]

      let response = null

      try {
        pipeline.push(
          {
            $addFields: {
              userRefObjectId: {
                $cond: [
                  { $and: [{ $ne: ['$userRef', null] }, { $ne: ['$userRef', ''] }, { $ne: ['$userRef', undefined] }] },
                  { $toObjectId: '$userRef' },
                  null
                ]
              },
              medicalProfRefObjectId: {
                $cond: [
                  {
                    $and: [
                      { $ne: ['$medicalProfRef', null] },
                      { $ne: ['$medicalProfRef', ''] },
                      { $ne: ['$medicalProfRef', undefined] }
                    ]
                  },
                  { $toObjectId: '$medicalProfRef' },
                  null
                ]
              }
            }
          },
          // Patient lookup
          createLookup(
            `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
            'userRefObjectId',
            '_id',
            'patientRecord',
            {
              firstName: 1,
              lastName: 1,
              idNo: 1,
              dateOfBirth: 1,
              gender: 1,
              race: 1,
              extraData: 1,
              residentialAddress: 1,
              email: 1,
              phoneNumber: 1,
              medicalAid: 1,
              mainMemberId: 1,
              billingId: 1
            }
          ),
          { $unwind: '$patientRecord' },

          // Medical professional lookup
          createLookup(
            `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
            'medicalProfRefObjectId',
            '_id',
            'medicalProfRecord',
            {
              firstName: 1,
              lastName: 1,
              'extraData.profession': 1
            }
          ),
          { $unwind: '$medicalProfRecord' }
        )

        // Add search text filtering after lookups
        if (searchText && searchText.trim()) {
          const searchRegex = { $regex: searchText.trim(), $options: 'i' }
          pipeline.push({
            $match: {
              $or: [
                { orderNumber: searchRegex },
                { 'patientRecord.firstName': searchRegex },
                { 'patientRecord.lastName': searchRegex },
                { 'patientRecord.idNo': searchRegex }
              ]
            }
          })
        }

        response = await agilite.Connectors.execute('lab_orders', 'aggregate', {
          pipeline: JSON.stringify(pipeline)
        })

        // Get the order numbers from the response
        const OrderArray = response.data.map((order) => {
          return {
            orderNumber: order.orderNumber,
            doctor: order.orderData.doctor
          }
        })

        const ampathResponse = await Axios.post(
          `${process.env.REACT_APP_NODE_RED_URL}/api/lab-orders/status`,
          {
            orders: OrderArray
          },
          {
            headers: {
              'api-key': process.env.REACT_APP_AGILITE_API_KEY
            }
          }
        )

        const orders = response.data.map((order) => {
          return {
            ...order,
            status: ampathResponse.data.orders.find((o) => o.orderNumber === order.orderNumber)?.orderStatus
          }
        })

        // Apply status filtering after getting status from Ampath
        if (status && status !== 'all') {
          const filteredOrders = orders.filter((order) => order.status === status)
          resolve(filteredOrders)
        } else {
          resolve(orders)
        }
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const createLabOrder = async (data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const addressObj = await geocodeByAddress(data.patient.residentialAddress)
        const { streetAddress, city } = extractAddressDetails(addressObj)

        const orderData = {
          // Adhoc
          orderNumber: '', // Blank on initial creation, will be set by ampath, to be used for updates if order is not already processed
          doctor: store.getState().auth.agiliteUser.extraData?.ampathId || '',
          collectionDate: data.collectionDate,
          collectionTime: data.collectionTime,
          priority: data.priority,
          icd10Codes: data.icd10Codes,
          orderedTests: data.orderedTests,
          informedConsentGiven: 'Y',
          // Patient Info
          guarantor: {
            name: `${data.patient.lastName},${data.patient.firstName}`,
            idNo: data.patient.idNo,
            addressline1: streetAddress,
            addresscity: city,
            cellphoneNo: data.patient.phoneNumber,
            homeTelNo: data.patient.phoneNumber,
            email: data.patient.email,
            language: 'E',
            medicalAidName: data.patient.medicalAid.name,
            medicalAidNo: data.patient.medicalAid.number,
            medicalAidDependantCode: data.patient.medicalAid.dependantNumber
          },
          patient: {
            age: Math.abs(dayjs(data.patient.dateOfBirth).diff(dayjs(), 'years')),
            dob: data.patient.dateOfBirth,
            gender: data.patient.gender === 'male' ? 'M' : 'F',
            idNo: data.patient.idNo,
            name: `${data.patient.lastName},${data.patient.firstName}`,
            title: generateTitle(data.patient.gender),
            cellNo: data.patient.phoneNumber,
            email: data.patient.email
          },
          admissionQuerries: [
            {
              code: '',
              value: ''
            }
          ],
          orderQuerries: [
            {
              code: '',
              value: ''
            }
          ],
          copyDoctors: ['']
        }

        // Create order on Ampath
        const ampathResponse = await Axios.post(
          `${process.env.REACT_APP_NODE_RED_URL}/api/lab-orders/create`,
          orderData,
          {
            headers: {
              'api-key': process.env.REACT_APP_AGILITE_API_KEY
            }
          }
        )

        if (ampathResponse.data.error && ampathResponse.data.error !== '') {
          throw new Error(ampathResponse.data.error)
        }

        const response = await agilite.Connectors.execute('lab_orders', 'create', {
          data: JSON.stringify({
            orderNumber: ampathResponse.data.orderNumber,
            userRef: data.patient._id,
            medicalProfRef: store.getState().auth.agiliteUser._id,
            orderData: orderData,
            createdAt: new Date().toISOString(),
            notes: data.notes
          })
        })

        resolve(response)
      } catch (e) {
        console.log('Error', e)
        reject(e)
      }
    })()
  })
}

export const cancelLabOrder = async (orderNumber, doctorId) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const ampathResponse = await Axios.post(
          `${process.env.REACT_APP_NODE_RED_URL}/api/lab-orders/cancel`,
          {
            orderNumber: orderNumber,
            doctor: doctorId
          },
          {
            headers: {
              'api-key': process.env.REACT_APP_AGILITE_API_KEY
            }
          }
        )

        resolve(ampathResponse.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getResultsByOrderNumber = async (orderNumber, includePDF = false, reportType = '') => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const ampathResponse = await Axios.post(
          `${process.env.REACT_APP_NODE_RED_URL}/api/lab-orders/results_by_order`,
          {
            orderNumber: orderNumber,
            includePDF,
            reportType
          },
          {
            headers: {
              'api-key': process.env.REACT_APP_AGILITE_API_KEY
            }
          }
        )

        resolve(ampathResponse.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}
